import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import * as IwDataDogLogger from '../../../iwLogger/IwDataDogLogger';
import { FormattedMessage } from 'react-intl';
import FundingHeader from '../fundingHeader/FundingHeader';
import messages from './ConfirmFundingView.messages';
import { Tooltip } from '../../ui/tooltip';
import styles from './ConfirmFundingView.module.css';
import { useEffect } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { ACTIVATE_CONFIRM_STEP } from '../Funding.actions';
import ConfirmForm from './confirmForm/ConfirmForm';
import { useGetFormalOffer } from '@api/lendingAPI/lapiHooks';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';
import FundingHeaderLoading from '../fundingHeader/FundingHeaderLoading';
import LoadingOverlay from '../fundingSteps/LoadingOverlay';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { trackPageView } from '../../../tracking/pageView';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import InterestOnlyExtensionConfirmForm from './confirmForm/InterestOnlyExtensionConfirmForm';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { FundingProgressIndicator } from '../fundingSteps/FundingProgressIndicator';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    activateConfirm: (
        offerId: string,
        drawdownAmount: number,
        sweep?: number
    ) => {
        dispatch({
            type: ACTIVATE_CONFIRM_STEP,
            offerId,
            amount: drawdownAmount,
            sweep
        });
    }
});

const ConfirmFundingView = ({
    activateConfirm
}: ReturnType<typeof mapDispatchToProps>) => {
    const stateKey = useStateKey();

    const location = getCurrentLocation();

    let drawdownAmount: string | undefined | number = getQueryParameter(
        location,
        'amount'
    );
    const sweepPercentage = getQueryParameter(location, 'sweep');
    const sweep = sweepPercentage ? parseFloat(sweepPercentage) : undefined;

    const offerId: string | undefined = getQueryParameter(location, 'offerId');

    useEffect(() => {
        if (drawdownAmount !== undefined && offerId) {
            activateConfirm(offerId, drawdownAmount as number, sweep);
        }
    }, [activateConfirm, offerId, drawdownAmount, sweep]);

    const { formalOffer, loadingFormalOffer } = useGetFormalOffer(offerId);

    const isInterestOnlyExtension =
        (formalOffer?.interest_only_repayments || 0) > 0 &&
        drawdownAmount === '0';

    const { isLoading: loadingAccounts, data: accounts } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        enabled: Boolean(stateKey)
    });

    if (!drawdownAmount) {
        IwDataDogLogger.logError(
            'Get Query Param drawdown amount is undefined',
            {
                tags: {
                    reporter: 'confirm_funding view'
                }
            }
        );
    }

    useEffect(() => trackPageView(stateKey, 'Confirmed Funding'), [stateKey]);

    if (!offerId) {
        IwDataDogLogger.logError('Get Query Param offerId is undefined', {
            tags: {
                reporter: 'confirm_funding view'
            }
        });
    }

    if (loadingFormalOffer || loadingAccounts) {
        return (
            <MainSectionWrapper>
                <FundingHeaderLoading />
                <div className={styles.container}>
                    <LoadingOverlay />
                </div>
            </MainSectionWrapper>
        );
    }

    drawdownAmount = parseFloat(drawdownAmount!);

    const hasAccountsData = accounts?.data;

    if (formalOffer && hasAccountsData) {
        const flexiApprovalTypes = ['flexi24', 'credit_facility'];

        const isTopUp = !!accounts.data!.accounts.find(
            (account) =>
                account.loan_status &&
                account.loan_status !== 'ended' &&
                (account.product_type === formalOffer.product_type ||
                    //topups can be any flexi type eg. Ongoing flexi24 can have credit_facility topup
                    (flexiApprovalTypes.includes(account.product_type!) &&
                        flexiApprovalTypes.includes(formalOffer.product_type!)))
        );

        return (
            <>
                {getCountry() === 'UK' && <FundingProgressIndicator step={3} />}

                <FundingHeader
                    header={
                        <FormattedMessage
                            {...(isInterestOnlyExtension
                                ? messages.interestOnlyHeader
                                : messages.header)}
                            values={{
                                tooltip: (
                                    <span>
                                        <Tooltip
                                            className={styles.tooltip}
                                            message={
                                                <FormattedMessage
                                                    {...messages.tooltip}
                                                />
                                            }
                                            placement="auto"
                                        />
                                    </span>
                                )
                            }}
                        />
                    }
                    subHeader={
                        <FormattedMessage
                            {...messages.interestOnlySubHeader}
                            {...(isInterestOnlyExtension
                                ? messages.interestOnlySubHeader
                                : messages.subHeader)}
                        />
                    }
                    returnPath="/funding-requirements/"
                />
                {isInterestOnlyExtension ? (
                    <InterestOnlyExtensionConfirmForm offerId={offerId!} />
                ) : (
                    <ConfirmForm
                        drawdownAmount={drawdownAmount}
                        selectedInputAmount={drawdownAmount}
                        selectedSweepPercentage={sweep!}
                        offerId={offerId!}
                        isTopUp={isTopUp}
                    />
                )}
            </>
        );
    }
    return null;
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(ConfirmFundingView);
